import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { EASE } from "@/components/Reveal";

const MaskLine = ({ children, delay, className = "" }) => (
  <div className="overflow-hidden">
    <motion.div
      className={className}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  </div>
);

export default function Hero({ onExplore, onDealer }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex h-[100svh] min-h-[640px] flex-col overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src="/images/hero.png"
          alt="Contractor wearing a CHWA tool backpack in cinematic light"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex h-full flex-col justify-between px-6 pb-10 pt-28 md:px-12 md:pb-14">
        <MaskLine delay={0.1}>
          <p className="text-xs uppercase tracking-[0.35em] text-zinc-400" data-testid="hero-eyebrow">
            Professional Work Gear — Texas, USA
          </p>
        </MaskLine>

        <div>
          <MaskLine delay={0.25}>
            <h1
              data-testid="hero-wordmark"
              className="font-display text-[clamp(5rem,21vw,23rem)] font-bold uppercase leading-[0.82] tracking-tight text-zinc-50"
            >
              CHWA
            </h1>
          </MaskLine>

          <div className="mt-6 grid gap-8 md:mt-10 md:grid-cols-2 md:items-end">
            <MaskLine delay={0.45}>
              <h2
                data-testid="hero-headline"
                className="font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tight text-zinc-100 sm:text-5xl lg:text-7xl"
              >
                Built for Work.
              </h2>
            </MaskLine>

            <div>
              <MaskLine delay={0.6}>
                <p data-testid="hero-subheadline" className="max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
                  Professional-grade gear engineered for contractors who demand more.
                </p>
              </MaskLine>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
                className="mt-8 flex flex-wrap items-center gap-4"
              >
                <button
                  data-testid="hero-explore-cta"
                  onClick={onExplore}
                  className="group flex items-center gap-3 bg-zinc-100 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-950 transition-colors duration-300 hover:bg-white"
                >
                  Explore Products
                  <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
                </button>
                <button
                  data-testid="hero-dealer-cta"
                  onClick={onDealer}
                  className="border border-zinc-600 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-100 transition-colors duration-300 hover:border-zinc-200"
                >
                  Become a Dealer
                </button>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
